import { useState, useCallback, useRef, useEffect } from 'react';
import { useDebounce } from './useDebounce';
import type { Course, CourseProgress, LessonProgress } from '../types/course';

const COMPLETION_THRESHOLD = 0.9;

interface UseCourseProgressOptions {
  course: Course;
  userId: string;
  initialProgress?: CourseProgress;
  onSave?: (
    courseId: string,
    lessonId: string,
    secondsWatched: number,
    completed: boolean
  ) => Promise<void>;
}

interface UseCourseProgressReturn {
  progress: CourseProgress;
  getLessonProgress: (lessonId: string) => LessonProgress | undefined;
  getCompletionPercent: (lessonId: string) => number;
  isCourseCompleted: () => boolean;
  updateTime: (
    lessonId: string,
    currentTime: number,
    durationSeconds: number
  ) => void;
  markLessonCompleted: (lessonId: string) => void;
  getResumeTime: (lessonId: string) => number;
  overallPercent: number;
}

function buildEmptyProgress(courseId: string): CourseProgress {
  return {
    courseId,
    lessons: {},
    lastLessonId: null,
    completionPercent: 0,
    startedAt: new Date().toISOString(),
    lastAccessedAt: new Date().toISOString(),
  };
}

function recalcCompletionPercent(
  course: Course,
  lessons: Record<string, LessonProgress>
): number {
  const total = course.lessons.length;
  if (total === 0) return 0;

  const completed = course.lessons.filter(
    (l) => lessons[l.id]?.completed
  ).length;

  return Math.round((completed / total) * 100);
}

export function useCourseProgress({
  course,
  userId: _userId,
  initialProgress,
  onSave,
}: UseCourseProgressOptions): UseCourseProgressReturn {
  const [progress, setProgress] = useState<CourseProgress>(
    () => initialProgress ?? buildEmptyProgress(course.id)
  );

  const debouncedProgress = useDebounce(progress, 5000);
  const onSaveRef = useRef(onSave);
  const courseRef = useRef(course);
  const lastSavedRef = useRef<Record<string, number>>({});

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    courseRef.current = course;
  }, [course]);

  // Reset dei salvataggi quando cambia corso
  useEffect(() => {
    lastSavedRef.current = {};
  }, [course.id]);

  // Sync dello stato locale con il corso/progresso proveniente dal parent
  useEffect(() => {
    if (!course.id) return;

    const nextProgress = initialProgress ?? buildEmptyProgress(course.id);
    setProgress(nextProgress);
  }, [course.id, initialProgress]);

  useEffect(() => {
    const saveCurrentLesson = async () => {
      if (!onSaveRef.current || !debouncedProgress.lastLessonId) return;

      const lessonId = debouncedProgress.lastLessonId;
      const lessonProgress = debouncedProgress.lessons[lessonId];
      if (!lessonProgress) return;

      const lastSaved = lastSavedRef.current[lessonId] || 0;
      const currentSeconds = lessonProgress.progressSeconds;

      if (currentSeconds !== lastSaved) {
        lastSavedRef.current[lessonId] = currentSeconds;

        await onSaveRef.current(
          debouncedProgress.courseId,
          lessonId,
          currentSeconds,
          lessonProgress.completed
        );
      }
    };

    saveCurrentLesson().catch(console.error);
  }, [debouncedProgress]);

  const updateTime = useCallback(
    (lessonId: string, currentTime: number, durationSeconds: number) => {
      const floored = Math.floor(currentTime);

      setProgress((prev) => {
        const existing = prev.lessons[lessonId];

        // FIX: non sovrascrivere un progresso esistente con 0
        // durante il bootstrap del player / primo timeupdate
        if (existing && existing.progressSeconds > 0 && floored === 0) {
          return prev;
        }

        if (existing && existing.progressSeconds === floored) {
          return prev;
        }

        const wasCompleted = existing?.completed ?? false;
        const isNowComplete =
          !wasCompleted &&
          durationSeconds > 0 &&
          currentTime / durationSeconds >= COMPLETION_THRESHOLD;

        const updated: LessonProgress = {
          lessonId,
          progressSeconds: floored,
          completed: wasCompleted || isNowComplete,
          lastWatchedAt: new Date().toISOString(),
        };

        const newLessons = {
          ...prev.lessons,
          [lessonId]: updated,
        };

        const c = courseRef.current;
        return {
          ...prev,
          courseId: prev.courseId || c.id,
          lessons: newLessons,
          lastLessonId: lessonId,
          lastAccessedAt: new Date().toISOString(),
          completionPercent: recalcCompletionPercent(c, newLessons),
        };
      });
    },
    [] // courseRef è stabile — aggiornato da un effect separato
  );

  const markLessonCompleted = useCallback((lessonId: string) => {
    setProgress((prev) => {
      if (prev.lessons[lessonId]?.completed) return prev;

      const existing = prev.lessons[lessonId];
      const updated: LessonProgress = {
        lessonId,
        progressSeconds: existing?.progressSeconds ?? 0,
        completed: true,
        lastWatchedAt: new Date().toISOString(),
      };

      const newLessons = {
        ...prev.lessons,
        [lessonId]: updated,
      };

      return {
        ...prev,
        lessons: newLessons,
        lastAccessedAt: new Date().toISOString(),
        completionPercent: recalcCompletionPercent(
          courseRef.current,
          newLessons
        ),
      };
    });
  }, []); // courseRef è stabile — aggiornato da un effect separato

  const getLessonProgress = useCallback(
    (lessonId: string) => progress.lessons[lessonId],
    [progress.lessons]
  );

  const getCompletionPercent = useCallback(
    (lessonId: string): number => {
      const lp = progress.lessons[lessonId];
      if (!lp) return 0;
      if (lp.completed) return 100;

      const lesson = course.lessons.find((l) => l.id === lessonId);
      if (!lesson || lesson.durationSeconds === 0) return 0;

      return Math.min(
        99,
        Math.round((lp.progressSeconds / lesson.durationSeconds) * 100)
      );
    },
    [progress.lessons, course.lessons]
  );

  const getResumeTime = useCallback(
    (lessonId: string): number => {
      const lp = progress.lessons[lessonId];
      if (!lp) return 0;
      if (lp.completed) return 0;
      return lp.progressSeconds ?? 0;
    },
    [progress.lessons]
  );

  const isCourseCompleted = useCallback(
    () => progress.completionPercent >= 100,
    [progress.completionPercent]
  );

  return {
    progress,
    getLessonProgress,
    getCompletionPercent,
    isCourseCompleted,
    updateTime,
    markLessonCompleted,
    getResumeTime,
    overallPercent: progress.completionPercent,
  };
}
